import { useMemo, useState } from 'react';
import { Activity, ChevronLeft, PackageCheck, Plus, Save, ShieldCheck, Trash2, X } from 'lucide-react';
import { createClinicReferral, saveClinicAction } from './data.js';
import { ageFromBirthDate, dateLabel, friendlyError } from './lib/format.js';

function Field({ label, required = false, wide = false, children, hint }) {
  return (
    <label className={`form-field ${wide ? 'form-field-wide' : ''}`}>
      <span>{label}{required ? ' *' : ''}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function localId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function blankItem(defaultNakesId = '') {
  return {
    local_id: localId(),
    service_id: '',
    nakes_id: defaultNakesId,
    assistant_nakes_id: '',
    notes: '',
    bhp: []
  };
}

function normalizeItem(item, defaultNakesId) {
  return {
    local_id: localId(),
    service_id: item.service_id || '',
    nakes_id: item.nakes_id || defaultNakesId || '',
    assistant_nakes_id: item.assistant_nakes_id || '',
    notes: item.notes || '',
    bhp: (item.bhp || []).map((row) => ({
      local_id: localId(),
      bhp_item_id: row.bhp_item_id || '',
      quantity_used: row.quantity_used ?? ''
    }))
  };
}

export function ActionFormModal({ data, onClose, onSaved, onGoToExamination }) {
  const { visit, client, medical_note: note = {} } = data;
  const estetikNote = data.estetik_note || null;
  const isEstetik = /^ESTETIK$/i.test(visit?.division || '');
  const defaultStaff = data.staff.find((row) => row.id === (estetikNote?.nakes_id || note.nakes_id))
    || data.staff.find((row) => row.name === (note.doctor_name || visit.doctor_name));
  const [items, setItems] = useState(() => (
    data.existing_items.length
      ? data.existing_items.map((item) => normalizeItem(item, defaultStaff?.id))
      : [blankItem(defaultStaff?.id)]
  ));
  const [improvementStatus, setImprovementStatus] = useState(note.improvement_status || '');
  const [actionNotes, setActionNotes] = useState(note.action_notes || '');
  const [referralDivision, setReferralDivision] = useState('');
  const [referralNakesId, setReferralNakesId] = useState('');
  const [referralReason, setReferralReason] = useState('');
  const [referralSummary, setReferralSummary] = useState('');
  const [referralPriority, setReferralPriority] = useState('NORMAL');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  // Filter tindakan berdasarkan divisi kunjungan
  // ALL = tersedia di semua divisi; divisi spesifik hanya tampil di divisi itu
  const visitDiv = (visit?.division || 'ALL').toUpperCase();
  const referralDivisions = [
    ['WELLNESS', 'Wellness / TCM'],
    ['ESTETIK', 'Estetik'],
    ['DENTAL', 'Dental'],
    ['DOKTER_UMUM', 'Dokter Umum'],
    ['FISIOTERAPI', 'Fisioterapi'],
    ['APOTIK', 'Apotik'],
  ].filter(([value]) => value !== visitDiv);
  const referralStaff = data.staff.filter((row) => (
    !referralDivision || (row.division || '').toUpperCase() === referralDivision
  ));
  const assistantStaff = data.staff.filter((row) => (
    String(row.division || '').toUpperCase() === 'WELLNESS'
  ));
  const filteredServices = useMemo(() =>
    data.services.filter((row) => {
      const d = (row.division || 'ALL').toUpperCase();
      return d === 'ALL' || d === visitDiv;
    }),
    [data.services, visitDiv]
  );
  const serviceMap = useMemo(() => Object.fromEntries(filteredServices.map((row) => [row.id, row])), [filteredServices]);
  const packageServiceIds = useMemo(() => new Set(data.package_service_ids || []), [data.package_service_ids]);
  const packageCapacityMap = useMemo(() => Object.fromEntries(
    (data.package_service_capacity || []).map((row) => [row.service_id, Number(row.available_actions || 0)])
  ), [data.package_service_capacity]);
  const selectedActionCount = items.filter((item) => item.service_id).length;
  const packageCoveredCount = useMemo(() => {
    const occurrences = {};
    return items.reduce((count, item) => {
      if (!item.service_id) return count;
      occurrences[item.service_id] = (occurrences[item.service_id] || 0) + 1;
      const capacity = packageCapacityMap[item.service_id]
        ?? (packageServiceIds.has(item.service_id) ? 1 : 0);
      return count + (occurrences[item.service_id] <= capacity ? 1 : 0);
    }, 0);
  }, [items, packageCapacityMap, packageServiceIds]);
  const bhpRowCount = items.reduce((sum, item) => sum + item.bhp.length, 0);

  function updateItem(localItemId, patch) {
    setItems((current) => current.map((item) => (
      item.local_id === localItemId ? { ...item, ...patch } : item
    )));
  }

  function addAction() {
    if (items.length >= 20) return setError('Maksimal 20 tindakan dalam satu kunjungan.');
    setError('');
    setItems((current) => [...current, blankItem(defaultStaff?.id)]);
  }

  function removeAction(localItemId) {
    if (items.length === 1) return setError('Minimal harus ada satu tindakan.');
    setItems((current) => current.filter((item) => item.local_id !== localItemId));
  }

  function addBhp(item) {
    const selected = new Set(item.bhp.map((row) => row.bhp_item_id));
    const available = data.bhp_items.find((row) => !selected.has(row.id));
    if (!available) return setError('Semua BHP aktif sudah ditambahkan pada tindakan ini.');
    updateItem(item.local_id, {
      bhp: [...item.bhp, { local_id: localId(), bhp_item_id: available.id, quantity_used: 1 }]
    });
  }

  function updateBhp(item, localBhpId, patch) {
    updateItem(item.local_id, {
      bhp: item.bhp.map((row) => row.local_id === localBhpId ? { ...row, ...patch } : row)
    });
  }

  function removeBhp(item, localBhpId) {
    updateItem(item.local_id, { bhp: item.bhp.filter((row) => row.local_id !== localBhpId) });
  }

  function validate(finalize) {
    if (!items.length) return 'Minimal pilih satu tindakan.';
    for (const [index, item] of items.entries()) {
      if (!item.service_id || !item.nakes_id) return `Tindakan dan pelaksana pada baris ${index + 1} wajib dipilih.`;
      const bhpIds = new Set();
      for (const bhp of item.bhp) {
        const quantity = Number(bhp.quantity_used);
        if (!bhp.bhp_item_id || !Number.isFinite(quantity) || quantity <= 0) return `Jumlah BHP pada baris ${index + 1} harus lebih dari nol.`;
        if (bhpIds.has(bhp.bhp_item_id)) return `BHP pada baris ${index + 1} tidak boleh diulang.`;
        bhpIds.add(bhp.bhp_item_id);
      }
    }
    if (finalize && !improvementStatus.trim()) return 'Status perbaikan wajib diisi sebelum finalisasi.';
    if (referralDivision && !referralReason.trim()) return 'Alasan rujukan wajib diisi.';
    return '';
  }

  async function submit(finalize) {
    const validationError = validate(finalize);
    setError(validationError);
    if (validationError) return;
    if (finalize && !window.confirm(
      'Finalisasi tindakan? Stok BHP akan langsung dikurangi, lalu kunjungan diteruskan ke kasir.'
    )) return;

    setBusy(finalize ? 'final' : 'draft');
    try {
      const payload = {
        improvement_status: improvementStatus,
        action_notes: actionNotes,
        items: items.map((item) => ({
          service_id: item.service_id,
          nakes_id: item.nakes_id,
          assistant_nakes_id: item.assistant_nakes_id || null,
          notes: item.notes,
          bhp: item.bhp.map((row) => ({
            bhp_item_id: row.bhp_item_id,
            quantity_used: row.quantity_used
          }))
        }))
      };
      const result = await saveClinicAction(visit.id, payload, finalize);
      if (referralDivision) {
        const targetStaff = referralStaff.find((row) => row.id === referralNakesId);
        await createClinicReferral({
          source_visit_id: visit.id,
          target_division: referralDivision,
          target_nakes_id: targetStaff?.id || null,
          target_doctor_name: targetStaff?.name || null,
          reason: referralReason,
          clinical_summary: referralSummary,
          priority: referralPriority,
        });
      }
      onSaved(
        result,
        finalize
          ? 'Tindakan selesai dan diteruskan ke Pembayaran.'
          : 'Draft tindakan dan BHP berhasil disimpan; stok BHP belum berubah.',
        finalize
      );
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card modal-card-action" role="dialog" aria-modal="true" aria-label="Tindakan dan BHP">
        <header className="modal-head">
          <div className="modal-title-icon"><Activity size={23} /></div>
          <div className="grow">
            <h2>Tindakan {isEstetik ? 'Estetik' : ''} & BHP</h2>
            <p>{visit.visit_number} · {dateLabel(visit.visit_date)} · {isEstetik ? 'estetik' : 'rekam medis'} {isEstetik ? (estetikNote?.draft_status || '') : note.draft_status}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Tutup"><X size={20} /></button>
        </header>

        <div className="modal-body action-body">
          <section className="patient-banner">
            <div>
              <span className="eyebrow">Pasien</span>
              <strong>{client.name}</strong>
              <small>{client.client_code} · {client.gender || 'JK belum diisi'} · {ageFromBirthDate(client.birth_date)}</small>
            </div>
            <div>
              <span className="eyebrow">{isEstetik ? 'Ringkasan estetik' : 'Ringkasan pemeriksaan'}</span>
              <strong>{isEstetik
                ? (estetikNote?.diagnosis || 'Diagnosis belum ditetapkan')
                : (note.syndrome_status || 'Sindrom belum ditetapkan')}
              </strong>
              <small>{isEstetik
                ? (estetikNote?.current_complaint || 'Keluhan belum tersedia')
                : (note.current_complaint || 'Keluhan belum tersedia')}
              </small>
            </div>
          </section>

          {error ? <div className="alert alert-danger">{error}</div> : null}
          <div className="alert alert-info compact-alert">
            <strong>Alur terpisah:</strong> nakes mencatat tindakan dan BHP. Sumber pembayaran ditentukan kasir pada menu Pembayaran.
          </div>

          <section className="action-list">
            {items.map((item, index) => {
              const serviceOccurrence = item.service_id
                ? items.slice(0, index + 1).filter((row) => row.service_id === item.service_id).length
                : 0;
              const packageCapacity = item.service_id
                ? (packageCapacityMap[item.service_id] ?? (packageServiceIds.has(item.service_id) ? 1 : 0))
                : 0;
              const coveredByActivePackage = packageCapacity > 0 && serviceOccurrence <= packageCapacity;
              const packageInsufficient = packageCapacity > 0 && serviceOccurrence > packageCapacity;
              return (
                <article className="action-card" key={item.local_id}>
                  <header className="action-card-head">
                    <div><span className="eyebrow">Tindakan {index + 1}</span><strong>{serviceMap[item.service_id]?.name || 'Belum dipilih'}</strong></div>
                    <button className="button button-mini button-danger-soft" onClick={() => removeAction(item.local_id)} disabled={!!busy}><Trash2 size={14} /> Hapus</button>
                  </header>
                  <div className="form-grid">
                    <Field label="Jenis tindakan" required>
                      <select value={item.service_id} onChange={(event) => updateItem(item.local_id, { service_id: event.target.value })} disabled={!!busy}>
                        <option value="">— Pilih tindakan —</option>
                        {filteredServices.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                      </select>
                    </Field>
                    <Field label="Pelaksana utama" required>
                      <select value={item.nakes_id} onChange={(event) => updateItem(item.local_id, { nakes_id: event.target.value })} disabled={!!busy}>
                        <option value="">— Pilih pelaksana —</option>
                        {data.staff.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                      </select>
                    </Field>
                    <Field label="Asisten Wellness" hint="Opsional. Hanya staf Wellness yang bertugas pada tanggal tindakan.">
                      <select value={item.assistant_nakes_id} onChange={(event) => updateItem(item.local_id, { assistant_nakes_id: event.target.value })} disabled={!!busy}>
                        <option value="">— Tanpa asisten —</option>
                        {assistantStaff.filter((row) => row.id !== item.nakes_id).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                      </select>
                    </Field>
                    <div className={`package-availability ${coveredByActivePackage ? 'package-availability-positive' : packageInsufficient ? 'package-availability-warning' : ''}`}>
                      <PackageCheck size={19} />
                      <div>
                        <span>Paket aktif sesuai tindakan</span>
                        <strong>{!item.service_id
                          ? 'Pilih tindakan terlebih dahulu'
                          : coveredByActivePackage
                            ? 'Tersedia untuk tindakan ini'
                            : packageInsufficient
                              ? 'Kuota tidak mencukupi'
                              : 'Tidak tersedia'}</strong>
                        <small>{packageCapacity > 0
                          ? `Kuota aktif untuk layanan ini: ${packageCapacity} tindakan. Sisanya diselesaikan kasir.`
                          : 'Pemilihan metode pembayaran dilakukan kasir.'}</small>
                      </div>
                    </div>
                    <Field label="Catatan tindakan">
                      <input value={item.notes} onChange={(event) => updateItem(item.local_id, { notes: event.target.value })} maxLength={1000} disabled={!!busy} />
                    </Field>
                  </div>

                  <div className="bhp-section">
                    <div className="bhp-section-head">
                      <div><strong>BHP digunakan</strong><span>Masukkan penggunaan aktual per tindakan.</span></div>
                      <button className="button button-mini button-quiet" onClick={() => addBhp(item)} disabled={!!busy || !data.bhp_items.length}><Plus size={14} /> Tambah BHP</button>
                    </div>
                    {item.bhp.length ? item.bhp.map((row) => {
                      const selectedBhp = data.bhp_items.find((bhp) => bhp.id === row.bhp_item_id);
                      return (
                        <div className="bhp-row" key={row.local_id}>
                          <select value={row.bhp_item_id} onChange={(event) => updateBhp(item, row.local_id, { bhp_item_id: event.target.value })} disabled={!!busy}>
                            {data.bhp_items.map((bhp) => (
                              <option key={bhp.id} value={bhp.id} disabled={item.bhp.some((other) => other.local_id !== row.local_id && other.bhp_item_id === bhp.id)}>
                                {bhp.name} · stok {bhp.current_stock} {bhp.unit}
                              </option>
                            ))}
                          </select>
                          <input type="number" min="1" max="100000" step="1" value={row.quantity_used} onChange={(event) => updateBhp(item, row.local_id, { quantity_used: event.target.value })} disabled={!!busy} aria-label="Jumlah BHP" />
                          <span>{selectedBhp?.unit || 'unit'}</span>
                          <button className="icon-button icon-button-danger" onClick={() => removeBhp(item, row.local_id)} disabled={!!busy} aria-label="Hapus BHP"><Trash2 size={16} /></button>
                        </div>
                      );
                    }) : <div className="bhp-empty">Tidak ada BHP dicatat untuk tindakan ini.</div>}
                  </div>
                </article>
              );
            })}
          </section>

          <button className="button button-soft add-action-button" onClick={addAction} disabled={!!busy}><Plus size={17} /> Tambah Tindakan</button>

          <section className="action-summary">
            <div><span>Jumlah tindakan</span><strong>{items.length}</strong></div>
            <div><span>Kecukupan paket aktif</span><strong><PackageCheck size={17} /> {selectedActionCount ? `${packageCoveredCount} dari ${selectedActionCount} tindakan` : 'Belum dihitung'}</strong></div>
            <div><span>BHP tercatat</span><strong>{bhpRowCount} baris</strong></div>
          </section>

          <fieldset className="clinical-fieldset" disabled={!!busy}>
            <legend>Hasil setelah tindakan</legend>
            <div className="form-grid">
              <Field label="Status perbaikan" required hint="Wajib sebelum finalisasi; draft boleh dikosongkan.">
                <input value={improvementStatus} onChange={(event) => setImprovementStatus(event.target.value)} maxLength={500} placeholder="Contoh: membaik, tidak berubah, perlu evaluasi" />
              </Field>
              <Field label="Tindak lanjut (untuk Follow Up)" wide>
                <textarea value={actionNotes} onChange={(event) => setActionNotes(event.target.value)} rows="4" maxLength={5000} />
              </Field>
            </div>
          </fieldset>

          <fieldset className="clinical-fieldset referral-fieldset" disabled={!!busy}>
            <legend>Rujukan ke poli lain <small>(opsional)</small></legend>
            <div className="form-grid">
              <Field label="Poli tujuan">
                <select value={referralDivision} onChange={(event) => { setReferralDivision(event.target.value); setReferralNakesId(''); }}>
                  <option value="">— Tidak membuat rujukan —</option>
                  {referralDivisions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              <Field label="Nakes tujuan">
                <select value={referralNakesId} onChange={(event) => setReferralNakesId(event.target.value)} disabled={!referralDivision}>
                  <option value="">— Pilih nakes (opsional) —</option>
                  {referralStaff.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                </select>
              </Field>
              <Field label="Prioritas">
                <select value={referralPriority} onChange={(event) => setReferralPriority(event.target.value)} disabled={!referralDivision}>
                  <option value="NORMAL">Normal</option>
                  <option value="URGENT">Segera</option>
                </select>
              </Field>
              <Field label="Alasan rujukan" required={!!referralDivision} wide>
                <input value={referralReason} onChange={(event) => setReferralReason(event.target.value)} disabled={!referralDivision} maxLength={500} placeholder="Contoh: perlu evaluasi nyeri lutut" />
              </Field>
              <Field label="Ringkasan untuk poli tujuan" wide>
                <textarea value={referralSummary} onChange={(event) => setReferralSummary(event.target.value)} disabled={!referralDivision} rows="3" maxLength={2000} placeholder="Ringkasan klinis yang perlu diketahui poli tujuan" />
              </Field>
            </div>
          </fieldset>
        </div>

        <footer className="modal-actions">
          <button
            className="button button-quiet"
            disabled={!!busy}
            onClick={onGoToExamination}
          >
            <ChevronLeft size={17} /> Edit SOAP
          </button>
          <button className="button button-quiet" onClick={onClose} disabled={!!busy}>Tutup</button>
          <button className="button button-soft" onClick={() => submit(false)} disabled={!!busy}>
            <Save size={17} /> {busy === 'draft' ? 'Menyimpan…' : 'Simpan Draft Tindakan'}
          </button>
          <button className="button button-primary" onClick={() => submit(true)} disabled={!!busy}>
            <ShieldCheck size={17} /> {busy === 'final' ? 'Memfinalkan…' : 'Finalisasi Tindakan'}
          </button>
        </footer>
      </section>
    </div>
  );
}
